/**
 * ad-group-orders-v2.js
 * Atelier Diamante — Group Orders interactive page
 *
 * Alpine component drives: live batch progress, tier ladder state,
 * pricing calculator, countdown timer, FAQ accordion, inquiry form.
 *
 * Live data is injected by the Liquid section via window.AD_GO config:
 *   - currentCount, batchTarget (progress)
 *   - tiers[] with thresholds + savings %
 *   - batchCloseDate (ISO string)
 */

(function () {
  'use strict';

  /* ----- Tier model (single source of truth) -----------------------------
     Game-style ladder: every 5 buyers unlocks the next tier and a deeper
     price drop. Thresholds are buyer counts (0,5,10,15,20). Overridable per
     campaign via window.AD_GO.tiers. */
  const DEFAULT_TIERS = [
    { key: 't1', name: 'Tier I',   pieces: '1–4 joined',  savings: 0,  threshold: 0  },
    { key: 't2', name: 'Tier II',  pieces: '5 joined',         savings: 5,  threshold: 5  },
    { key: 't3', name: 'Tier III', pieces: '10 joined',        savings: 10, threshold: 10 },
    { key: 't4', name: 'Tier IV',  pieces: '15 joined',        savings: 13, threshold: 15 },
    { key: 't5', name: 'Tier V',   pieces: '20 joined',        savings: 15, threshold: 20 },
  ];

  /* ----- Currency formatter ---------------------------------------------- */
  const usd = function (n) {
    if (isNaN(n) || n == null) return '$0';
    return '$' + Math.round(n).toLocaleString('en-US');
  };

  /* ----- Date helpers ---------------------------------------------------- */
  function nextReleaseDay() {
    /* Mon=1, Wed=3, Fri=5 — finds the next release weekday from today */
    var now = new Date();
    var dow = now.getDay(); /* 0=Sun..6=Sat */
    var releases = [1, 3, 5];
    var todayIdx = releases.indexOf(dow);
    if (todayIdx > -1) {
      /* today is a release day — show today */
      return { dayName: ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][dow], date: now };
    }
    /* find next */
    for (var add = 1; add <= 7; add++) {
      var test = (dow + add) % 7;
      if (releases.indexOf(test) > -1) {
        var d = new Date(now);
        d.setDate(d.getDate() + add);
        return { dayName: ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][test], date: d };
      }
    }
    return { dayName: 'Mon', date: now };
  }

  function formatShortDate(d) {
    if (!d) return '';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  /* ----- Immersive 360: drag-to-rotate video scrubber --------------------
     The Manon turntable video doubles as a 360 viewer. Idle = gentle autoplay
     loop; grab and drag horizontally to scrub through the rotation by hand.
     Resumes the ambient loop after you let go. */
  function initManon360() {
    var stage = document.querySelector('[data-manon360]');
    if (!stage || stage._wired) return;
    var video = stage.querySelector('[data-manon360-video]');
    if (!video) return;
    stage._wired = true;

    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var SENS = 1.15;           /* drag sensitivity (full width ≈ one full turn) */
    var RESUME_MS = 2600;      /* idle before ambient loop resumes */
    var dragging = false, decided = false, isTouch = false;
    var startX = 0, startY = 0, startTime = 0, resumeId = null;
    var AXIS = 6; /* px of movement before we decide: horizontal-scrub vs vertical-scroll */

    video.muted = true; video.loop = true; video.playsInline = true;
    video.setAttribute('playsinline', '');

    /* Browsers ignore <source media>, so pick the clip here: phones keep the
       light SD source (the default <source>); only wider viewports upgrade to HD. */
    var hd = video.getAttribute('data-src-hd');
    if (hd && window.innerWidth >= 750 && (video.currentSrc || '').indexOf(hd) === -1) {
      video.src = hd; video.load();
    }

    function ambient() {
      if (reduce || dragging) return;
      var p = video.play();
      if (p && p.catch) p.catch(function () {}); /* autoplay may be blocked — drag still works */
    }
    function scheduleResume() { clearTimeout(resumeId); resumeId = setTimeout(ambient, RESUME_MS); }
    function scrubTo(clientX) {
      var dur = video.duration;
      if (!dur || isNaN(dur)) return;
      var w = stage.getBoundingClientRect().width || 1;
      var t = (startTime + ((clientX - startX) / w) * SENS * dur) % dur;
      if (t < 0) t += dur;
      video.currentTime = t;
    }
    function commitDrag(clientX) {
      dragging = true;
      stage.classList.add('is-grabbing');
      stage.classList.add('has-interacted');
      video.pause();
      startX = clientX;                  /* reset origin so the scrub doesn't jump */
      startTime = video.currentTime || 0;
      clearTimeout(resumeId);
    }
    function begin(clientX, clientY, touch) {
      isTouch = touch; startX = clientX; startY = clientY; startTime = video.currentTime || 0;
      decided = !touch;                  /* mouse: intent is immediate */
      dragging = !touch;                 /* touch: wait to see which way they move */
      if (!touch) commitDrag(clientX);
    }
    function moveTo(clientX, clientY, e) {
      if (isTouch && !decided) {
        var dx = Math.abs(clientX - startX), dy = Math.abs(clientY - startY);
        if (dx < AXIS && dy < AXIS) return;       /* not enough movement to decide yet */
        decided = true;
        if (dx > dy) { commitDrag(clientX); }     /* horizontal → scrub the 360 */
        else { return; }                          /* vertical → leave it; the page scrolls */
      }
      if (!dragging) return;
      scrubTo(clientX);
      if (e && e.cancelable && isTouch) e.preventDefault();
    }
    function end() {
      if (dragging) { stage.classList.remove('is-grabbing'); scheduleResume(); }
      dragging = false; decided = false; isTouch = false;
    }

    stage.addEventListener('mousedown', function (e) { begin(e.clientX, e.clientY, false); });
    window.addEventListener('mousemove', function (e) { moveTo(e.clientX, e.clientY, e); });
    window.addEventListener('mouseup', end);
    stage.addEventListener('touchstart', function (e) { begin(e.touches[0].clientX, e.touches[0].clientY, true); }, { passive: true });
    stage.addEventListener('touchmove', function (e) { moveTo(e.touches[0].clientX, e.touches[0].clientY, e); }, { passive: false });
    stage.addEventListener('touchend', end);
    stage.addEventListener('touchcancel', end);

    if (video.readyState >= 1) ambient();
    else video.addEventListener('loadedmetadata', ambient, { once: true });
  }

  /* ----- Alpine component ------------------------------------------------ */
  document.addEventListener('alpine:init', function () {
    if (typeof Alpine === 'undefined') return;

    Alpine.data('adGroupOrders', function () {
      var cfg = window.AD_GO || {};
      return {
        /* ----- state ----- */
        tiers: (cfg.tiers && cfg.tiers.length) ? cfg.tiers : DEFAULT_TIERS,
        currentCount: parseInt(cfg.currentCount, 10) || 0,
        batchTarget:  parseInt(cfg.batchTarget,  10) || 20,
        batchCloseISO: cfg.batchCloseDate || '',

        /* Featured campaign (the immersive 360 product) */
        campaign: cfg.campaign || {},

        /* Live tracking */
        liveStatusUrl: cfg.liveStatusUrl || '',
        storefront: cfg.storefront || {},     /* Shopify Storefront API (no backend) */
        livePollMs: parseInt(cfg.livePollMs, 10) || 8000,
        _liveId: null,
        isLive: false,
        pulse: false,        /* flashes when a new buyer joins */
        tierUnlocked: false, /* celebration when a tier threshold is crossed */

        /* Calculator */
        calcValue: parseInt(cfg.calcDefault, 10) || 25000,

        /* Timer */
        days: 0, hours: 0, mins: 0, secs: 0, timerExpired: false, _timerId: null,

        /* FAQ */
        openFaq: null,

        /* Form */
        submitting: false, submitted: false, errorMsg: '',

        /* ----- lifecycle ----- */
        init: function () {
          this.startTimer();
          this.startLivePolling();
          this.$nextTick(function () { initManon360(); });
        },

        /* ----- computed: tier helpers ----- */
        get currentTier() {
          /* Highest tier whose threshold has been reached */
          var t = this.tiers[0];
          for (var i = 0; i < this.tiers.length; i++) {
            if (this.currentCount >= this.tiers[i].threshold) t = this.tiers[i];
          }
          return t;
        },
        get currentTierIndex() {
          var idx = 0, k = this.currentTier && this.currentTier.key;
          for (var i = 0; i < this.tiers.length; i++) if (this.tiers[i].key === k) idx = i;
          return idx;
        },
        get nextTier() {
          var i = this.currentTierIndex;
          return (i < this.tiers.length - 1) ? this.tiers[i + 1] : null;
        },
        tierState: function (i) {
          if (i === this.currentTierIndex) return 'current';
          if (i < this.currentTierIndex)   return 'unlocked';
          return 'locked';
        },
        tierStatusLabel: function (i) {
          var s = this.tierState(i);
          if (s === 'current')  return 'Current Tier';
          if (s === 'unlocked') return 'Unlocked';
          var t = this.tiers[i];
          var needed = Math.max(0, t.threshold - this.currentCount);
          return needed + ' more ' + (needed === 1 ? 'piece' : 'pieces') + ' to unlock';
        },

        /* ----- progress bar ----- */
        get progressPercent() {
          if (this.batchTarget <= 0) return 0;
          return Math.min(100, Math.round((this.currentCount / this.batchTarget) * 100));
        },
        get spotsRemaining() {
          return Math.max(0, this.batchTarget - this.currentCount);
        },

        /* ----- pricing calculator ----- */
        priceAtTier: function (tier) {
          var v = parseFloat(this.calcValue) || 0;
          return v * (1 - (tier.savings / 100));
        },
        savedAtTier: function (tier) {
          var v = parseFloat(this.calcValue) || 0;
          return v * (tier.savings / 100);
        },
        formatPrice: function (n) { return usd(n); },
        savingsLabel: function (tier) {
          if (tier.savings === 0) return 'Standard rate';
          return 'Save ' + tier.savings + '%';
        },

        /* ----- featured campaign (360 product) pricing ----- */
        get campaignBase() { return parseFloat(this.campaign.basePrice) || 0; },
        get currentUnitPrice() {
          return this.campaignBase * (1 - (this.currentTier.savings / 100));
        },
        get nextUnlockPrice() {
          var nt = this.nextTier;
          return nt ? this.campaignBase * (1 - (nt.savings / 100)) : null;
        },
        get buyersToNextTier() {
          var nt = this.nextTier;
          return nt ? Math.max(0, nt.threshold - this.currentCount) : 0;
        },
        get depositAmount() { return parseFloat(this.campaign.depositAmount) || 0; },
        currentPriceLabel: function () { return usd(this.currentUnitPrice); },
        nextPriceLabel: function () {
          return this.nextUnlockPrice == null ? '' : usd(this.nextUnlockPrice);
        },

        /* ----- join / reserve (deposit) -----
           Payment engine ships next phase. For now: if a join/deposit URL is
           configured, send the buyer there; otherwise scroll to the inquiry
           form so no one hits a dead end. */
        joinNow: function () {
          var url = this.campaign.joinUrl;
          if (url) { window.location.href = url; return; }
          var el = document.getElementById('ad-go-inquiry');
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        },

        /* ----- live buyer tracking (polling) -----
           Polls liveStatusUrl for { count, batchTarget?, closeDate? }. Polling
           (not WebSocket) is plenty for batch cadence and far cheaper to run.
           Falls back silently to the seeded count if the endpoint is absent. */
        startLivePolling: function () {
          var self = this;
          var sf = this.storefront || {};
          var poll;

          if (sf.token && sf.shopDomain && sf.productHandle) {
            /* Shopify Storefront API — no backend. Reads the product's
               group_order_count metafield directly from the browser. The
               storefront token is public by design; CORS is allowed. */
            /* Prefer the page's own origin (same-origin → no CORS); fall back to
               the configured shop domain if origin is unavailable. */
            var origin = (window.location && /^https?:/.test(window.location.origin)) ? window.location.origin : '';
            var endpoint = (origin || 'https://' + sf.shopDomain) + '/api/2024-10/graphql.json';
            var ns = sf.namespace || 'custom';
            var key = sf.key || 'group_order_count';
            var query = 'query($h:String!){product(handle:$h){metafield(namespace:"' +
                        ns + '",key:"' + key + '"){value}}}';
            poll = function () {
              fetch(endpoint, {
                method: 'POST',
                cache: 'no-store',
                headers: { 'Content-Type': 'application/json',
                           'X-Shopify-Storefront-Access-Token': sf.token },
                body: JSON.stringify({ query: query, variables: { h: sf.productHandle } })
              })
              .then(function (r) { return r.ok ? r.json() : null; })
              .then(function (d) {
                var mf = d && d.data && d.data.product && d.data.product.metafield;
                var n = (mf && mf.value != null) ? parseInt(mf.value, 10) : 0;
                self.isLive = true;
                self._applyCount(isNaN(n) ? 0 : n);
              })
              .catch(function () { /* keep last known count; retry next tick */ });
            };
          } else if (this.liveStatusUrl) {
            /* Fallback: custom JSON endpoint returning { count, batchTarget?, closeDate? } */
            poll = function () {
              fetch(self.liveStatusUrl, { headers: { 'Accept': 'application/json' }, cache: 'no-store' })
                .then(function (r) { return r.ok ? r.json() : null; })
                .then(function (data) {
                  if (!data || typeof data.count === 'undefined') return;
                  self.isLive = true;
                  if (typeof data.batchTarget !== 'undefined') self.batchTarget = parseInt(data.batchTarget, 10) || self.batchTarget;
                  if (data.closeDate && data.closeDate !== self.batchCloseISO) {
                    self.batchCloseISO = data.closeDate;
                    clearInterval(self._timerId); self.timerExpired = false; self.startTimer();
                  }
                  self._applyCount(parseInt(data.count, 10));
                })
                .catch(function () {});
            };
          } else {
            return; /* no live source configured — stays on seeded count */
          }

          poll();
          this._liveId = setInterval(poll, this.livePollMs);
        },
        _applyCount: function (n) {
          if (isNaN(n) || n === this.currentCount) return;
          var prevTierIdx = this.currentTierIndex;
          var joined = n > this.currentCount;
          this.currentCount = n;
          if (joined) {
            var self = this;
            this.pulse = true;
            setTimeout(function () { self.pulse = false; }, 1200);
            if (this.currentTierIndex > prevTierIdx) {
              this.tierUnlocked = true;
              setTimeout(function () { self.tierUnlocked = false; }, 3200);
            }
          }
        },

        /* ----- countdown timer ----- */
        startTimer: function () {
          var self = this;
          if (!this.batchCloseISO) return;
          var target = new Date(this.batchCloseISO).getTime();
          if (isNaN(target)) return;

          var tick = function () {
            var diff = target - Date.now();
            if (diff <= 0) {
              self.timerExpired = true;
              self.days = self.hours = self.mins = self.secs = 0;
              clearInterval(self._timerId);
              return;
            }
            self.days  = Math.floor(diff / 86400000);
            self.hours = Math.floor((diff % 86400000) / 3600000);
            self.mins  = Math.floor((diff % 3600000) / 60000);
            self.secs  = Math.floor((diff % 60000) / 1000);
          };
          tick();
          this._timerId = setInterval(tick, 1000);
        },
        pad: function (n) { return n < 10 ? '0' + n : '' + n; },

        /* ----- schedule next release ----- */
        get nextRelease() {
          var r = nextReleaseDay();
          return { dayName: r.dayName, date: formatShortDate(r.date) };
        },
        isNextRelease: function (dayName) {
          return this.nextRelease.dayName === dayName;
        },

        /* ----- FAQ ----- */
        toggleFaq: function (key) {
          this.openFaq = (this.openFaq === key) ? null : key;
        },

        /* ----- form submit ----- */
        submitInquiry: function (e) {
          if (this.submitting) return;
          var form = e.target;
          this.submitting = true;
          this.errorMsg = '';

          /* Build a readable contact[body] from all form fields
             so Dave's email shows all inquiry details neatly */
          var get = function (id) {
            var el = document.getElementById(id);
            return (el && el.value) ? el.value : 'Not provided';
          };
          var bodyLines = [
            'GROUP ACQUISITION INQUIRY',
            '─────────────────────────────',
            'Budget Range:   ' + get('ad-go-budget'),
            'Pieces:         ' + get('ad-go-pieces'),
            'Timeline:       ' + get('ad-go-timeline'),
            '',
            'Notes / Vision:',
            get('ad-go-notes'),
          ];

          /* Submit via fetch so we can show inline success without page reload.
             Shopify contact form action posts to /contact#contact_form */
          var fd = new FormData(form);
          /* Compose a readable body for the email */
          fd.set('contact[body]', bodyLines.join('\n'));
          /* Tag the submission so it routes to the right inbox */
          fd.append('form_type', 'contact');
          fd.append('utf8', '✓');
          // contact[tags] is ignored by Shopify — group-order marker lives in contact[body] prefix

          var self = this;
          fetch('/contact', {
            method: 'POST',
            body: fd
            // No Accept header — Shopify returns HTML; parse to detect error state
          })
          .then(function (r) {
            return r.text().then(function (html) {
              // Shopify always returns 200 OK even on validation failure
              if (/class="[^"]*\bform__message--error\b/.test(html) ||
                  /class="[^"]*\bcontact-form--error\b/.test(html) ||
                  /class="[^"]*\bform-status--error\b/.test(html)) {
                throw new Error('VALIDATION_FAILED');
              }
              return html;
            });
          })
          .then(function () {
            self.submitted = true;
            form.reset();
            /* Scroll the success banner into view */
            setTimeout(function () {
              var el = document.getElementById('ad-go-success');
              if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 100);
          })
          .catch(function (err) {
            console.error('Group Orders inquiry submit failed:', err);
            if (err && err.message === 'VALIDATION_FAILED') {
              self.errorMsg = 'Please check the required fields and try again. If this keeps happening, email dave@atelierdiamante.com.';
            } else {
              self.errorMsg = "Sorry, we couldn't submit your inquiry. Please email dave@atelierdiamante.com directly.";
            }
          })
          .finally(function () {
            self.submitting = false;
          });

          e.preventDefault();
        },
      };
    });
  });
})();

